import {Component, OnInit} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import {OtherContentService} from "../../../core/services/other-content.service";
import {AppUtils} from "../../../core/config/app.utils";
import {ActionAlert} from "../../../core/dto/actionAlert";
import {LoadingService} from "../../../core/services/loading.service";
import {LanguageService} from "../../../core/services/language.service";
import {SettingService} from "../../../core/services/setting.service";
import {AuthService} from "../../../modules/auth/services/auth.service";
import {OpenAiService} from "../../../core/services/open-ai.service";
import { SETTINGS } from '../../../core/config/common.settings';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: "app-other-content",
  templateUrl: "./other-content.component.html",
  styleUrls: ["./other-content.component.scss"],
})

export class OtherContentComponent implements OnInit {
  otherContentForm: any;
  otherContent: any;
  formErrors: any;
  contentId: any;
  actionMessage = new ActionAlert();
  isAdd = false;
  activeTabIndex = 0;
  // language
  language: any = "en";
  languageType: any;
  selectedLanguages: any = [];
  languages: any = [];

  constructor(
    private fb: FormBuilder,
    private otherContentService: OtherContentService,
    private loading: LoadingService,
    private languageService: LanguageService,
    private settingService: SettingService,
    private authService: AuthService,
    private openAiService: OpenAiService,
    private translocoService: TranslocoService,
  ) {
    this.formErrors = {
      exclusions: {},
      inclusions: {},
      policies: {},
    };
  }

  ngOnInit(): void {
    this.loading.start();
    this.otherContentForm = this.fb.group({
      exclusions: [''],
      inclusions: [''],
      policies: [''],
    });

    this.findBrand().then();
    this.getLanguages().then(() => {
      this.languageType = this.languages?.find((l: any) => l.code === this.language);
    });
    this.findAllOtherContent().then(() => this.loading.stop());

    this.otherContentForm.get("inclusions")?.valueChanges.subscribe((value: any) => {
      if (this.otherContentForm.get("inclusions").dirty) {
        this.AddContentBasedOnLang(value, this.language, "INCLU");
      }
    });

    this.otherContentForm.get("exclusions")?.valueChanges.subscribe((value: any) => {
      if (this.otherContentForm.get("exclusions").dirty) {
        this.AddContentBasedOnLang(value, this.language, "EXCLU");
      }
    });

    this.otherContentForm.get("policies")?.valueChanges.subscribe((value: any) => {
      if (this.otherContentForm.get("policies").dirty) {
        this.AddContentBasedOnLang(value, this.language, "POLIC");
      }
    });

    this.otherContentForm.valueChanges.subscribe((form: any) => {
      AppUtils.getFormErrors(this.otherContentForm, this.formErrors);
    });
  }

  activeTabField(): string {
    switch (this.activeTabIndex) {
      case 0: return 'inclusions';
      case 1: return 'exclusions';
      case 2: return 'policies';
      default: return 'inclusions';
    }
  }

  activeTabContentType(): string {
    switch (this.activeTabIndex) {
      case 0: return 'INCLU';
      case 1: return 'EXCLU';
      case 2: return 'POLIC';
      default: return 'INCLU';
    }
  }

  hasEnglishContent(): boolean {
    const field = this.activeTabField();
    const arr = this.otherContent?.[0]?.[field];
    return !!(arr?.find((l: any) => l?.language === 'en')?.content);
  }

  async createOrUpdateOtherContent(buttonName: string): Promise<any> {
    this.loading.start();

    if (this.contentId === undefined || null) {
      try {
        const created = await this.otherContentService.createOtherContent({
          ...this.otherContent[0]
        });
        this.otherContent = [created];
        this.contentId = this.otherContent[0]?._id;
        this.findContentTextFromLanguage(this.language);
        this.otherContentForm.markAsPristine();
        this.actionMessage = new ActionAlert(
          true,
          !this.isAdd,
          this.translocoService.translate('otherContent.messages.contentCreated'),
          "success"
        );
        this.loading.stop();
      } catch (e) {
        this.actionMessage = new ActionAlert(
          true,
          !this.isAdd,
          this.translocoService.translate('otherContent.messages.contentNotCreated'),
          "error"
        );
        this.loading.stop();
      }
    } else {
      let successMessageKey = '';
      let errorMessageKey = '';
      switch (buttonName) {
        case "EXCLU":
          successMessageKey = 'otherContent.messages.exclusionUpdated';
          errorMessageKey = 'otherContent.messages.exclusionNotUpdated';
          break;
        case "INCLU":
          successMessageKey = 'otherContent.messages.inclusionUpdated';
          errorMessageKey = 'otherContent.messages.inclusionNotUpdated';
          break;
        case "POLIC":
          successMessageKey = 'otherContent.messages.termsUpdated';
          errorMessageKey = 'otherContent.messages.termsNotUpdated';
          break;
      }
      try {
        const updated = await this.otherContentService.updateOtherContent({
          ...this.otherContent[0]
        });
        this.otherContent = [updated];
        this.findContentTextFromLanguage(this.language);
        this.otherContentForm.markAsPristine();
        this.actionMessage = new ActionAlert(
          true,
          !this.isAdd,
          this.translocoService.translate(successMessageKey),
          "success"
        );
        this.loading.stop();
      } catch (e) {
        this.actionMessage = new ActionAlert(
          true,
          !this.isAdd,
          this.translocoService.translate(errorMessageKey),
          "error"
        );
        this.loading.stop();
      }
    }
  }

  async findAllOtherContent(): Promise<any> {
    this.otherContent = await this.otherContentService.findAllOtherContent();
    this.contentId = this.otherContent[0]?._id;
    if (this.contentId !== undefined) {
      this.findContentTextFromLanguage(this.language);
    } else {
      this.otherContent[0] = {
        inclusions: [],
        exclusions: [],
        policies: [],
      };
    }
  }

  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
    this.actionMessage = new ActionAlert(false);
    this.findContentTextFromLanguage(this.language);
  }

  getSelectedLanguagesObjectArray(): any {
    const arr: any = [];
    this.selectedLanguages?.forEach((lang: string) => {
      arr.push(this.languages?.find((l: any) => l.code === lang));
    });
    return arr;
  }

  async getLanguages(): Promise<any> {
    this.languages = await this.languageService.getContentLanguages();
  }

  async findBrand(): Promise<void> {
    const user = await this.authService.getLoggedInUser();
    const brand = await this.settingService.findBrand(user.userId);
    this.selectedLanguages = brand?.selectedLanguages || [];
    return this.selectedLanguages;
  }

  onChangeLanguage(lang: string): void {
    // Snapshot current form values into the local array for the outgoing language
    // only if the user actually edited something, so switching back restores correctly.
    if (this.otherContentForm.dirty) {
      const currentInc = this.otherContentForm.get('inclusions')?.value;
      const currentExc = this.otherContentForm.get('exclusions')?.value;
      const currentPol = this.otherContentForm.get('policies')?.value;
      if (currentInc != null) {
        this.AddContentBasedOnLang(currentInc, this.language, 'INCLU');
      }
      if (currentExc != null) {
        this.AddContentBasedOnLang(currentExc, this.language, 'EXCLU');
      }
      if (currentPol != null) {
        this.AddContentBasedOnLang(currentPol, this.language, 'POLIC');
      }
    }
    this.language = lang;
    this.languageType = this.languages?.find((l: any) => l.code === lang);
    this.findContentTextFromLanguage(lang);
  }

  findContentTextFromLanguage(language: string): any {
    const inc = this.otherContent[0]?.inclusions.find((l: any) => l?.language === language);
    const exc = this.otherContent[0]?.exclusions.find((l: any) => l?.language === language);
    const pol = this.otherContent[0]?.policies.find((l: any) => l?.language === language);
    this.otherContentForm.markAsPristine();
    this.otherContentForm.patchValue({
        inclusions: inc?.content || "",
        exclusions: exc?.content || "",
        policies: pol?.content || ""
      }
    );
  }

  AddContentBasedOnLang(content: string, language: string, contentType: string): void {
    switch (contentType) {
      case "EXCLU":
        const excIndex = this.otherContent[0]?.exclusions.findIndex((l: any) => l?.language === language);
        if (excIndex !== -1) {
          this.otherContent[0].exclusions[excIndex] = {content, language};
        } else {
          this.otherContent[0].exclusions.push({content, language});
        }
        break;
      case "INCLU":
        const incIndex = this.otherContent[0]?.inclusions.findIndex((l: any) => l?.language === language);
        if (incIndex !== -1) {
          this.otherContent[0].inclusions[incIndex] = {content, language};
        } else {
          this.otherContent[0].inclusions.push({content, language});
        }
        break;
      case "POLIC":
        const polIndex = this.otherContent[0]?.policies.findIndex((l: any) => l?.language === language);
        if (polIndex !== -1) {
          this.otherContent[0].policies[polIndex] = {content, language};
        } else {
          this.otherContent[0].policies.push({content, language});
        }
        break;
    }
  }

  async translateContent(content: string, language: string, type: string): Promise<void> {
    this.loading.start();
      const payload = `
        You are a translation engine.

        Translate the text content inside the provided HTML into '${language}'.

        Rules:
        - Preserve all HTML tags exactly.
        - Do not modify HTML structure or attributes.
        - Only translate text nodes.
        - Do not add comments or explanations.
        - Output format: plain text HTML only.
        - Never use markdown or code blocks.

        HTML:
        ${content}
        `;
      try {
        const value = await this.openAiService.askChatGPT({query: payload});
        if (value) {
          const newContent = value.choices[0].message.content;
          // Mark pristine before patching so the valueChanges dirty-guard does not
          // call AddContentBasedOnLang and corrupt the local array entry.
          this.otherContentForm.markAsPristine();
          if (type === 'inclusions') {
            this.otherContentForm.patchValue({inclusions: newContent});
            this.AddContentBasedOnLang(newContent, this.language, 'INCLU');
          } else if (type === 'exclusions') {
            this.otherContentForm.patchValue({exclusions: newContent});
            this.AddContentBasedOnLang(newContent, this.language, 'EXCLU');
          } else if (type === 'policies') {
            this.otherContentForm.patchValue({policies: newContent});
            this.AddContentBasedOnLang(newContent, this.language, 'POLIC');
          }
        }
        this.loading.stop();
      } catch (error) {
        this.loading.stop();
      }
  }

  useEnglishContent(): void {
    const field = this.activeTabField();
    const contentType = this.activeTabContentType();
    const arr = this.otherContent?.[0]?.[field];
    const englishEntry = arr?.find((l: any) => l?.language === 'en');
    if (englishEntry?.content) {
      this.otherContentForm.markAsPristine();
      this.otherContentForm.patchValue({ [field]: englishEntry.content });
      this.AddContentBasedOnLang(englishEntry.content, this.language, contentType);
    }
  }

  protected readonly SETTINGS = SETTINGS;
}
